import React, { useEffect, useMemo, useReducer } from 'react';
import clsx from 'clsx';
import styles from './index.module.scss';
import StepDescription from 'routes/Driver/ScanPage/StepDescription';
import {
  LicenseCameraScanStepsEnum,
  hideFlipMessageAction,
  initialState,
  nextStepAction,
  previousStepAction,
  reducer,
  setImageStatusAction,
  setImageUloadProgressAction,
  setUseManualUploadAction,
} from './reducer';
import VideoStream from 'components/VideoStream';
import ImagePreview, { ImagePreviewStatus } from 'components/ImagePreview';
import ManualUploadButton from '../ManualUploadButton';
import FlipIdToCaptureBack from 'features/Driver/LicenseCameraScan/FlipIdToCaptureBack';
import documentRequestCandidateService from 'store/document-request-candidate/service';
import { DriverDocumentsPage, TemporaryLicensePage } from 'routes/paths';
import { useNavigate } from 'react-router-dom';
import { useGetUploadLicenseStatus } from './useGetUploadLicenseStatus';
import {
  DocumentProcessStatusFieldsEnum,
  documentProcessErrorsStatus,
  DocumentProcessStatusEnum,
} from 'common/document/enums/document-status.enum';
import { b64toFile } from 'functions/file/b64toFile';
import { UploadImage } from 'components/UploadImage';
import { fileTob64 } from 'functions/file/fileTob64';
import MessageBanner from 'components/MessageBanner';
import { DocumentTypeEnum } from 'common/document/enums/document-type.enum';
import {
  FOCUS_ERROR_MESSAGE,
  PREDEFINED_ERROR_MESSAGES,
} from 'components/VideoStream/errorMessages';
import { useFeatureFlags } from '../../../contexts/featureFlag/useFeatureFlags';
import { useDeviceFeatures } from 'hooks/useDeviceFeatures';
import { ESegmentCategory, track } from 'integrations/analytics';
import { DocumentVerificationUpdated } from 'common/document/interfaces/document-verification-updated-message';
import { useLogsManager } from 'integrations/logs';

export interface LicenseCameraScanProps {
  documentRequestId: string;
  documentId: string;
  className?: string;
  useManulaUpload?: boolean;
}

const frontSteps = [
  LicenseCameraScanStepsEnum.licenseFrontScan,
  LicenseCameraScanStepsEnum.licenseFrontPreview,
];
const backSteps = [
  LicenseCameraScanStepsEnum.licenseBackScan,
  LicenseCameraScanStepsEnum.licenseBackPreview,
];
const cameraSteps = [
  LicenseCameraScanStepsEnum.licenseFrontScan,
  LicenseCameraScanStepsEnum.licenseBackScan,
];
const previewSteps = [
  LicenseCameraScanStepsEnum.licenseFrontPreview,
  LicenseCameraScanStepsEnum.licenseBackPreview,
];

function LicenseCameraScan(props: LicenseCameraScanProps) {
  const navigate = useNavigate();
  const logs = useLogsManager();
  const [mediaStreamTrack, setMediaStreamTrack] =
    React.useState<MediaStreamTrack>();

  const { featureFlags } = useFeatureFlags();
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    useManualUpload: props.useManulaUpload,
  });

  const deviceFeatures = useDeviceFeatures(mediaStreamTrack);

  const [getUrlToUploadFile] =
    documentRequestCandidateService.endpoints.getPresignedUrlForUploading.useLazyQuery();

  const [checkPreProcessingStatus] =
    documentRequestCandidateService.endpoints.checkPreProcessingStatus.useLazyQuery();

  const uploadLicenseApi = useGetUploadLicenseStatus();

  const showFrontTexts = useMemo(
    () => frontSteps.includes(state.step),
    [state.step]
  );
  const showBackTexts = useMemo(
    () => backSteps.includes(state.step),
    [state.step]
  );
  const showCamera = useMemo(
    () => cameraSteps.includes(state.step),
    [state.step]
  );
  const showPreview = useMemo(
    () => previewSteps.includes(state.step),
    [state.step]
  );

  const onProcessing = () => {
    dispatch(setImageStatusAction(ImagePreviewStatus.PROCESSING));
  };

  const onProgressing = (progress: number) => {
    dispatch(setImageUloadProgressAction(progress));
  };

  const onProcessed = (documentStatus: DocumentVerificationUpdated) => {
    if (documentStatus?.verificationStatus) {
      const status = documentProcessErrorsStatus.includes(
        documentStatus.verificationStatus
      )
        ? ImagePreviewStatus.ERROR
        : ImagePreviewStatus.SUCCESS;
      const defaultMessage = (PREDEFINED_ERROR_MESSAGES as any)[
        documentStatus.verificationStatus
      ];

      if (
        documentStatus?.verificationStatus === DocumentProcessStatusEnum.FAILED
      ) {
        checkPreProcessingStatus({ documentId: props.documentId })
          .unwrap()
          .then((preProcessingStatuses: any) => {
            const preProcessingStatusMap = new Map<string, any>(
              Object.entries(preProcessingStatuses)
            );
            if (
              state.step === LicenseCameraScanStepsEnum.licenseFrontPreview &&
              preProcessingStatusMap.has(
                DocumentProcessStatusFieldsEnum.DRIVER_LICENSE_FRONT_PRE_PROCESSING_STATUS
              )
            ) {
              const documentStatus = preProcessingStatusMap.get(
                DocumentProcessStatusFieldsEnum.DRIVER_LICENSE_FRONT_PRE_PROCESSING_STATUS
              );
              const defaultMessage = (PREDEFINED_ERROR_MESSAGES as any)[
                documentStatus.verificationStatus
              ];
              dispatch(
                setImageStatusAction(
                  ImagePreviewStatus.SUCCESS,
                  defaultMessage?.message,
                  defaultMessage?.label
                )
              );
            } else if (
              state.step === LicenseCameraScanStepsEnum.licenseBackPreview &&
              preProcessingStatusMap.has(
                DocumentProcessStatusFieldsEnum.DRIVER_LICENSE_BACK_PRE_PROCESSING_STATUS
              )
            ) {
              const documentStatus = preProcessingStatusMap.get(
                DocumentProcessStatusFieldsEnum.DRIVER_LICENSE_FRONT_PRE_PROCESSING_STATUS
              );
              const defaultMessage = (PREDEFINED_ERROR_MESSAGES as any)[
                documentStatus.verificationStatus
              ];
              dispatch(
                setImageStatusAction(
                  ImagePreviewStatus.SUCCESS,
                  defaultMessage?.message,
                  defaultMessage?.label
                )
              );
            } else {
              dispatch(
                setImageStatusAction(
                  status,
                  defaultMessage?.message,
                  defaultMessage?.label
                )
              );
            }
          })
          .catch(() => {
            dispatch(
              setImageStatusAction(
                status,
                defaultMessage?.message,
                defaultMessage?.label
              )
            );
          });
      } else {
        dispatch(
          setImageStatusAction(
            status,
            defaultMessage?.message,
            defaultMessage?.label
          )
        );
      }
    }
  };

  const imageToShowPreview = useMemo(() => {
    if (state.imagePreview) {
      return state.imagePreview;
    }
    return state.step === LicenseCameraScanStepsEnum.licenseFrontPreview
      ? state.licenseFront
      : state.licenseBack;
  }, [state.step, state.licenseFront, state.licenseBack]);

  useEffect(() => {
    if (state.step === LicenseCameraScanStepsEnum.licenseBackScan) {
      setTimeout(() => dispatch(hideFlipMessageAction()), 3000);
    }
  }, [state.step]);

  useEffect(() => {
    (async () => {
      if (
        [
          LicenseCameraScanStepsEnum.licenseFrontPreview,
          LicenseCameraScanStepsEnum.licenseBackPreview,
        ].includes(state.step) &&
        featureFlags['VALIDATE_BLUR_SCORE'] &&
        state.focusQuality &&
        (state.focusQuality < state.focusQualityThreshold! ||
          state.isBlankImage ||
          state.isWhiteImage)
      ) {
        dispatch(
          setImageStatusAction(
            ImagePreviewStatus.ERROR,
            FOCUS_ERROR_MESSAGE.message,
            FOCUS_ERROR_MESSAGE.label
          )
        );
        const trackData = {
          type: DocumentTypeEnum.DRIVER_LICENSE,
          manualUpload: state.useManualUpload,
          deviceFeatures,
          validateBlurScore: featureFlags['VALIDATE_BLUR_SCORE'],
          focusQuality: state.focusQuality,
          focusQualityThreshold: state.focusQualityThreshold,
          documentId: props.documentId,
          documentRequestId: props.documentRequestId,
        };
        track(ESegmentCategory.IMAGE_OUT_OF_FOCUS, {
          type: DocumentTypeEnum.DRIVER_LICENSE,
          manualUpload: state.useManualUpload,
          deviceFeatures,
          validateBlurScore: featureFlags['VALIDATE_BLUR_SCORE'],
          focusQuality: state.focusQuality,
          focusQualityThreshold: state.focusQualityThreshold,
          documentId: props.documentId,
          documentRequestId: props.documentRequestId,
        });
        logs.logWarning('Image out of focus', trackData);
        return;
      }

      if (state.step === LicenseCameraScanStepsEnum.licenseFrontPreview) {
        let url: any = {};
        try {
          url = await getUrlToUploadFile({
            documentId: props.documentId,
            fileTypes: state?.file?.type ? [state?.file?.type] : ['image/png'],
          }).unwrap();
        } catch (e: any) {
          dispatch(
            setImageStatusAction(ImagePreviewStatus.ERROR, e.message, e.message)
          );
          console.error(e);
        }

        if (url?.front) {
          const file =
            state.file ||
            b64toFile(state.licenseFront!, 'image/png', 'LicenseFront');
          uploadLicenseApi.uploadDocument(
            file,
            url.front,
            DocumentProcessStatusFieldsEnum.DRIVER_LICENSE_FRONT_PRE_PROCESSING_STATUS,
            onProgressing,
            onProcessing,
            onProcessed
          );

          track(ESegmentCategory.IMAGE_UPLOADED, {
            type: DocumentTypeEnum.DRIVER_LICENSE,
            subType: 'front',
            manualUpload: state.useManualUpload,
            deviceFeatures,
            validateBlurScore: featureFlags['VALIDATE_BLUR_SCORE'],
            focusQuality: state.focusQuality,
            focusQualityThreshold: state.focusQualityThreshold,
          });
        }
      }

      if (state.step === LicenseCameraScanStepsEnum.licenseBackPreview) {
        let url: any = {};
        try {
          url = await getUrlToUploadFile({
            documentId: props.documentId,
            fileTypes: state?.file?.type ? [state?.file?.type] : ['image/png'],
          }).unwrap();
        } catch (e: any) {
          dispatch(
            setImageStatusAction(ImagePreviewStatus.ERROR, e.message, e.message)
          );
          console.error(e);
        }

        if (url?.back) {
          const file =
            state.file ||
            b64toFile(state.licenseBack!, 'image/png', 'LicenseBack');
          uploadLicenseApi.uploadDocument(
            file,
            url.back,
            DocumentProcessStatusFieldsEnum.DRIVER_LICENSE_BACK_PRE_PROCESSING_STATUS,
            onProgressing,
            onProcessing,
            onProcessed
          );
          track(ESegmentCategory.IMAGE_UPLOADED, {
            type: DocumentTypeEnum.DRIVER_LICENSE,
            subType: 'back',
            manualUpload: state.useManualUpload,
            deviceFeatures,
            validateBlurScore: featureFlags['VALIDATE_BLUR_SCORE'],
            focusQuality: state.focusQuality,
            focusQualityThreshold: state.focusQualityThreshold,
          });
        }
      }

      if (state.step === LicenseCameraScanStepsEnum.temporaryLicense) {
        navigate(
          `${TemporaryLicensePage.replaceAll(
            ':documentRequestId',
            props.documentRequestId
          ).replaceAll(':documentId', props.documentId)}?from=${
            DocumentTypeEnum.DRIVER_LICENSE
          }`
        );
      }

      if (state.step === LicenseCameraScanStepsEnum.endOfTheFlow) {
        navigate(
          `${DriverDocumentsPage}?from=${DocumentTypeEnum.DRIVER_LICENSE}`
        );
      }
    })();
  }, [getUrlToUploadFile, state.step]);

  const onImageCapture = (
    base64Image: string,
    focusQuality: number,
    focusQualityThreshold: number,
    isBlankImage?: boolean,
    isWhiteImage?: boolean
  ) => {
    dispatch(
      nextStepAction({
        mainImage: base64Image,
        focusQuality: focusQuality!,
        focusQualityThreshold,
        isBlankImage,
        isWhiteImage,
      })
    );
    track(ESegmentCategory.CAMERA_UPLOAD_IMAGE_SELECTED);
  };

  const onContinueClick = () => {
    dispatch(nextStepAction());
  };

  const onRetakeClick = () => {
    dispatch(previousStepAction());
    track(ESegmentCategory.RETAKE_PHOTO);
  };

  const handleFrontFileUploaded = async (
    file?: File | Blob,
    previewImage?: string
  ) => {
    const base64Image = await fileTob64(file as File, true);

    dispatch(
      nextStepAction(
        {
          mainImage: base64Image,
          focusQuality: 0,
          focusQualityThreshold: -1,
          file: file as File,
        },
        previewImage
      )
    );
    track(ESegmentCategory.MANUAL_UPLOAD_IMAGE_SELECTED);
  };

  const onMediaTrackChanged = (mediaStreamTrack?: MediaStreamTrack) => {
    setMediaStreamTrack(mediaStreamTrack);
  };

  return (
    <div className={clsx(props.className)}>
      {showFrontTexts && (
        <StepDescription
          title={
            state.useManualUpload
              ? 'Upload Front of License'
              : 'Capture License Front'
          }
          description={
            state.useManualUpload
              ? 'Please upload a photo of the front of your License'
              : 'Position the front of your License in the frame.'
          }
        />
      )}

      {showBackTexts && (
        <StepDescription
          title={
            state.useManualUpload
              ? 'Upload Back of License'
              : 'Capture License Back'
          }
          description={
            state.useManualUpload
              ? 'Please upload a photo of the back of your License'
              : 'Position the back of your License in the frame.'
          }
        />
      )}

      {showCamera && !state.useManualUpload && (
        <div className={styles.videoContainer}>
          <VideoStream
            aspectRatio="card"
            withTarget
            onImageCapture={onImageCapture}
            onMediaTrackChanged={onMediaTrackChanged}
            disableCameraButton={state.showFlipMessage}
            useMultiplePhotos={featureFlags['USE_MULTIPLE_PHOTOS']}
            calculateBlurScore={featureFlags['VALIDATE_BLUR_SCORE']}
          />
          {state.showFlipMessage && (
            <FlipIdToCaptureBack className={styles.flipCaptureMessage} />
          )}
        </div>
      )}

      {showCamera && state.useManualUpload && (
        <>
          <UploadImage
            noEditStep
            key={'front'}
            uploadedFileUrl={''}
            onChange={handleFrontFileUploaded}
            placeholder={''}
          />
          <MessageBanner className={styles.manualUpladMessage}>
            Make sure photo is on a flat well lit surface and all edges are
            visible.
          </MessageBanner>
        </>
      )}

      {showCamera && !state.useManualUpload && (
        <ManualUploadButton
          onClick={() => dispatch(setUseManualUploadAction(true))}
          showMessageBeforeTimeout
          className={styles.manualUploadButton}
          key={state.step}
          documentRequestId={props.documentRequestId}
          documentId={props.documentId}
          documentType="license"
          withTimeout
          documentTypeEnum={DocumentTypeEnum.DRIVER_LICENSE}
        />
      )}

      {showPreview && (
        <div>
          <ImagePreview
            file={state.file}
            documentTypeEnum={DocumentTypeEnum.DRIVER_LICENSE}
            hideManualUploadButton={state.useManualUpload}
            documentType="license"
            onManualUploadButtonClick={() => {
              dispatch(setUseManualUploadAction(true));
              dispatch(previousStepAction());
            }}
            documentRequestId={props.documentRequestId}
            documentId={props.documentId}
            source={imageToShowPreview}
            status={state.imageStatus}
            imageUploadProgress={state.imageUploadProgress}
            errorMessage={state.imageErrorMessage}
            errorLabel={state.imageErrorLabel}
            buttons={{
              hideRetake: state.imageStatus !== ImagePreviewStatus.SUCCESS,
              onApproveClick:
                state.imageStatus === ImagePreviewStatus.SUCCESS
                  ? onContinueClick
                  : onRetakeClick,
              onRetakeClick: onRetakeClick,
              approveText:
                state.imageStatus === ImagePreviewStatus.SUCCESS
                  ? 'Next'
                  : state.useManualUpload
                  ? 'Select image'
                  : 'Retake Photo',
            }}
          />
        </div>
      )}
    </div>
  );
}

export default LicenseCameraScan;
